import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import type { Database } from '../lib/database.types'

type PoliceFeedItem = Database['public']['Tables']['police_feed']['Row']

interface PoliceFeedStore {
  items: PoliceFeedItem[]
  loading: boolean
  error: string | null
  lastFetchedAt: number
  
  // Actions
  fetchPoliceFeed: (residenceCity?: string) => Promise<void>
  clearError: () => void
}

export const usePoliceFeedStore = create<PoliceFeedStore>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  lastFetchedAt: 0,

  fetchPoliceFeed: async (residenceCity?: string) => {
    try {
      set({ loading: true, error: null })
      
      const { lastFetchedAt } = get()
      const now = Date.now()
      const CACHE_DURATION = 5 * 60 * 1000 // 5 minutter
      
      // Sjekk cache hvis nylig hentet
      if (now - lastFetchedAt < CACHE_DURATION) {
        set({ loading: false })
        return
      }

      let query = supabase
        .from('police_feed')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(20) // Begrens til 20 nyeste meldinger

      // Filtrer på municipality hvis residenceCity er gitt
      if (residenceCity) {
        console.log('Filtrerer på municipality for:', residenceCity)
        
        // Definer nærliggende kommuner for hver by
        const nearbyMunicipalities: Record<string, string[]> = {
          'Oslo': ['Oslo', 'Lillestrøm', 'Frogn', 'Ås', 'Nordre Follo', 'Enebakk', 'Lørenskog', 'Nittedal', 'Rælingen', 'Skiptvet', 'Spydeberg', 'Trøgstad', 'Vestby', 'Oppegård'],
          'Bergen': ['Bergen', 'Alver', 'Bjørnafjorden', 'Samnanger', 'Øygarden'],
          'Trondheim': ['Trondheim', 'Malvik', 'Skaun', 'Orkland', 'Melhus'],
          'Stavanger': ['Stavanger', 'Sola', 'Sandnes', 'Hå', 'Klepp', 'Time', 'Gjesdal', 'Strand'],
          'Kristiansand': ['Kristiansand', 'Vennesla', 'Søgne', 'Songdalen', 'Marnardal', 'Lindesnes']
        }
        
        const nearby = nearbyMunicipalities[residenceCity] || [residenceCity]
        console.log('Nærliggende kommuner:', nearby)
        
        // Søk etter bynavn eller nærliggende kommuner
        const municipalityFilter = nearby.map(m => `municipality.ilike.%${m}%`).join(',')
        query = query.or(municipalityFilter)
      } else {
        console.log('Henter alle politiet-feed data uten filtrering')
      }

      const { data: filteredData, error: queryError } = await query

      // Hvis filtrering gir ingen resultater, hent alle nyheter som fallback
      if (residenceCity && (!filteredData || filteredData.length === 0)) {
        console.log('Ingen resultater for filtrering, henter alle nyheter som fallback')
        const { data: allData, error: fallbackError } = await supabase
          .from('police_feed')
          .select('*')
          .order('published_at', { ascending: false })
          .limit(20)
        
        if (fallbackError) {
          console.error('Feil ved fallback henting:', fallbackError)
          set({ error: 'Kunne ikke hente nyheter fra politiet', loading: false })
          return
        }
        
        set({
          items: allData || [],
          loading: false,
          lastFetchedAt: now
        })
        return
      }

      const data = filteredData
      const error = queryError

      console.log('Police feed query result:', { 
        dataCount: data?.length || 0, 
        error, 
        residenceCity,
        sampleData: data?.slice(0, 3).map(item => ({ 
          title: item.title, 
          municipality: item.municipality,
          district: item.district 
        }))
      })

      if (error) {
        console.error('Feil ved henting av politiet-feed:', error)
        set({ error: 'Kunne ikke hente nyheter fra politiet', loading: false })
        return
      }

      set({
        items: data || [],
        loading: false,
        lastFetchedAt: now
      })

    } catch (error) {
      console.error('Uventet feil ved henting av politiet-feed:', error)
      set({ 
        error: 'En uventet feil oppstod ved henting av nyheter', 
        loading: false 
      })
    }
  },

  clearError: () => set({ error: null })
}))
